import type { SubscriptionAdditionalOption } from '~/types/subscription'
import { defineStore } from 'pinia'
import { useSubscriptionOfferStore } from '~/stores/subscription-offer'

export const useSubscriptionExtrasStore = defineStore('subscription-extras', () => {
  const selectedCoverageIdentifier = ref<string | undefined>(undefined)
  const selectedAdditionalOptionIds = reactive<Set<number>>(new Set())
  const coverageUpsellShown = ref(false)
  const extrasVisited = ref(false)
  const bookingUuid = ref<string | undefined>(undefined)
  const additionalDriverCode = ref<string | undefined>(undefined)

  const selectedCoverage = computed<SubscriptionAdditionalOption | undefined>(() => {
    if (!selectedCoverageIdentifier.value)
      return undefined
    const options = useSubscriptionOfferStore().selectedOffer?.additional_options ?? []
    return options.find(o => o.tags?.includes(`${selectedCoverageIdentifier.value}_insurance`))
  })

  const totalExtraPrice = computed<number>(() => {
    const options = useSubscriptionOfferStore().selectedOffer?.additional_options ?? []
    const additionalOptionsPrice = options
      .filter(o => selectedAdditionalOptionIds.has(o.id))
      .reduce((sum, o) => sum + o.price, 0)
    return (selectedCoverage.value?.price ?? 0) + additionalOptionsPrice
  })

  function selectCoverage(identifier: string) {
    selectedCoverageIdentifier.value = identifier
  }

  function toggleOption(id: number) {
    if (selectedAdditionalOptionIds.has(id))
      selectedAdditionalOptionIds.delete(id)
    else
      selectedAdditionalOptionIds.add(id)
  }

  function isOptionSelected(id: number) {
    return selectedAdditionalOptionIds.has(id)
  }

  function markCoverageUpsellShown() {
    coverageUpsellShown.value = true
  }

  function reset() {
    selectedCoverageIdentifier.value = undefined
    selectedAdditionalOptionIds.clear()
    coverageUpsellShown.value = false
    extrasVisited.value = false
    bookingUuid.value = undefined
    additionalDriverCode.value = undefined
  }

  return {
    selectedCoverageIdentifier,
    selectedAdditionalOptionIds,
    selectedCoverage,
    totalExtraPrice,
    extrasVisited,
    bookingUuid,
    additionalDriverCode,
    coverageUpsellShown: readonly(coverageUpsellShown),
    selectCoverage,
    toggleOption,
    isOptionSelected,
    markCoverageUpsellShown,
    reset,
  }
})
